import { useState } from 'react';
import { Users, Tag, Award, Send } from 'lucide-react';
import type { PermissionKey } from '@supernova/shared';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';
import { CustomerListSection } from './customers/customer-list';
import { CouponListSection } from './coupons/coupon-list';
import { RewardProgramListSection } from './reward-programs/reward-program-list';
import { CampaignListSection } from './campaigns/campaign-list';

type Section = 'customers' | 'coupons' | 'reward-programs' | 'campaigns';

const sections: { key: Section; label: string; icon: typeof Users; view: PermissionKey }[] = [
  { key: 'customers', label: 'Customers', icon: Users, view: 'customers.view' },
  { key: 'coupons', label: 'Coupons', icon: Tag, view: 'coupons.view' },
  { key: 'reward-programs', label: 'Reward Programs', icon: Award, view: 'rewards.view' },
  { key: 'campaigns', label: 'Campaigns', icon: Send, view: 'campaigns.view' },
];

/**
 * Customer Engagement page — surfaces inside the POS Management tab strip.
 * Sections switch via the standard horizontal tab strip (same style as the
 * Reports / Reconciliation pages). Each section is a separate file under
 * apps/web/src/pages/customer-engagement/.
 *
 * Visibility of the parent tab is gated by the per-location runtime toggle
 * `location_pos_config.customer_engagement_enabled` (see PosManagementPage)
 * which a super admin flips from POS Management → POS Config → Customer
 * Engagement. Server-side endpoints are independently gated by
 * CUSTOMER_ENGAGEMENT_ENABLED on pos-backend (env-level kill switch).
 */
export function CustomerEngagementPage() {
  const permissions = usePermissions();
  const visible = sections.filter((s) => permissions.has(s.view));
  const [picked, setActiveSection] = useState<Section>('customers');
  // A section the user can't view falls back to the first one they can.
  const activeSection = visible.some((s) => s.key === picked) ? picked : visible[0]?.key;

  return (
    <div className="space-y-4">
      {/* Section tabs — standard report-page tab style. */}
      <div className="scrollbar-hide overflow-x-auto border-b border-border">
        <div className="flex w-full min-w-0" role="tablist" aria-label="Customer Management sections">
          {visible.map(({ key, label, icon: Icon }) => {
            const isSelected = activeSection === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={isSelected}
                onClick={() => setActiveSection(key)}
                className={cn(
                  'relative flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-4 py-2.5 text-center text-xs font-medium transition-colors sm:text-sm',
                  isSelected ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
                {isSelected && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-w-0">
        {activeSection === 'customers' && <CustomerListSection />}
        {activeSection === 'coupons' && <CouponListSection />}
        {activeSection === 'reward-programs' && <RewardProgramListSection />}
        {activeSection === 'campaigns' && <CampaignListSection />}
      </div>
    </div>
  );
}

export default CustomerEngagementPage;
